import React, { useCallback, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import AccessLevelService, { siteModerator } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import FirebaseStore from 'stores/FirebaseStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import {
  isAnyTypeOfGold,
  ACCESS_LEVEL_STR,
} from 'pages/User/AccessLevel';
import { CHAT_AVATAR_URL } from 'containers/Chat/Api';

import nnLogo from '../../../../../../../images/logo/nnlogo.png';
import temptedLogo from '../../../../../../../images/logo/temptedlogo.png';

import RecentSpendingTierLegend from './RecentSpendingTierLegend';
import UserListItem from './UserListItem/UserListItem';
import styles from './UserListTab.css';


const getProfileImage = (username) => {
  const encodedUsername = encodeURIComponent(username).toLowerCase();
  const imageUrl = CHAT_AVATAR_URL.replace('{username}', encodedUsername);

  return imageUrl;
};

const sortUsersByTier = (a, b) => {
  if (a.recentSpendingTier === b.recentSpendingTier) {
    return 0;
  }

  if (a.recentSpendingTier < b.recentSpendingTier) {
    return 1;
  }

  return -1;
};

const sortUsersForViewers = (users) => {
  const goldFanclubUsers = [];
  const goldUsers = [];
  const fanclubUsers = [];
  const otherUsers = [];

  for (const currentUser of users) {
    const { accessLevel, isFanclubSubscribed } = currentUser;
    const isSubscribed = Boolean(parseInt(isFanclubSubscribed, 10));
    const isGold = isAnyTypeOfGold(accessLevel);

    if (isGold && isSubscribed) {
      goldFanclubUsers.push(currentUser);
    } else if (isGold) {
      goldUsers.push(currentUser);
    } else if (isSubscribed) {
      fanclubUsers.push(currentUser);
    } else {
      otherUsers.push(currentUser);
    }
  }
  const sortedUsers = [
    ...goldFanclubUsers,
    ...goldUsers,
    ...fanclubUsers,
    ...otherUsers,
  ];

  return sortedUsers;
};

const sortUsersForBroadcaster = (users) => {
  const goldFanclubUsers = {
    withTokens: [],
    withoutTokens: [],
  };
  const goldUsers = {
    withTokens: [],
    withoutTokens: [],
  };
  const fanclubUsers = {
    withTokens: [],
    withoutTokens: [],
  };
  const otherUsers = {
    withTokens: [],
    withoutTokens: [],
  };

  for (const currentUser of users) {
    const { accessLevel, hasTokens, isFanclubSubscribed } = currentUser;
    const isSubscribed = Boolean(parseInt(isFanclubSubscribed, 10));
    const isGold = isAnyTypeOfGold(accessLevel);

    const subGroup = hasTokens ? 'withTokens' : 'withoutTokens';

    if (isGold && isSubscribed) {
      goldFanclubUsers[subGroup].push(currentUser);
    } else if (isGold) {
      goldUsers[subGroup].push(currentUser);
    } else if (isSubscribed) {
      fanclubUsers[subGroup].push(currentUser);
    } else {
      otherUsers[subGroup].push(currentUser);
    }
  }

  goldFanclubUsers.withTokens.sort(sortUsersByTier);
  goldFanclubUsers.withoutTokens.sort(sortUsersByTier);

  goldUsers.withTokens.sort(sortUsersByTier);
  goldUsers.withoutTokens.sort(sortUsersByTier);

  fanclubUsers.withTokens.sort(sortUsersByTier);
  fanclubUsers.withoutTokens.sort(sortUsersByTier);

  otherUsers.withTokens.sort(sortUsersByTier);
  otherUsers.withoutTokens.sort(sortUsersByTier);

  const sortedUsers = [
    ...goldFanclubUsers.withTokens,
    ...goldFanclubUsers.withoutTokens,
    ...goldUsers.withTokens,
    ...goldUsers.withoutTokens,
    ...fanclubUsers.withTokens,
    ...fanclubUsers.withoutTokens,
    ...otherUsers.withTokens,
    ...otherUsers.withoutTokens,
  ];

  return sortedUsers;
};

const UserListTab = (props) => {
  const {
    isBroadcasterChat,
    isRecentSpendingTierLegendOpen,
    isUserList,
    username,
    onToggleRecentSpendingTierLegend,
  } = props;

  const { user } = UserStore;
  const { sortedUsers: storeUsers, externalViewersCount } = FirebaseStore;

  const userCountLight = storeUsers.length;
  const profileName = isBroadcasterChat ? username : ProfileStore.profileName;
  const isMinSiteModerator = AccessLevelService.isMin(siteModerator, user.accessLevel);

  const filterForModerators = useCallback((currentUser) => {
    const { originalUsername } = currentUser;
    const isPerformer = originalUsername.toLowerCase() === profileName.toLowerCase();

    return !isPerformer;
  }, [profileName]);

  const filterForRegularViewers = useCallback((currentUser) => {
    const { originalUsername } = currentUser;
    const isPerformer = originalUsername.toLowerCase() === profileName.toLowerCase();
    const isVisible = Number(currentUser.stealthLevel) === 0;
    const isGuest = currentUser.accessLevel === ACCESS_LEVEL_STR.GUEST;
    const showUser = !isPerformer && isVisible && !isGuest;

    return showUser;
  }, [profileName]);

  const finalUserList = useMemo(() => {
    let users = undefined;
    if (isMinSiteModerator) {
      users = storeUsers.filter(filterForModerators);
    } else {
      users = storeUsers.filter(filterForRegularViewers);
    }

    const sortedUsers = isBroadcasterChat ? sortUsersForBroadcaster(users) : sortUsersForViewers(users);

    return sortedUsers;
  }, [
    filterForModerators,
    filterForRegularViewers,
    isBroadcasterChat,
    isMinSiteModerator,
    storeUsers,
  ]);

  const mapUserToItem = useCallback((currentUser) => {
    const {
      accessLevel,
      userID,
      hasTokens,
      isBroadcasting,
      isFanclubSubscribed,
      isRoomModerator,
      isSilenced,
      originalUsername,
      recentSpendingTier,
    } = currentUser;

    const isStarred = isAnyTypeOfGold(accessLevel);
    const profileImage = getProfileImage(originalUsername);

    return (
      <UserListItem
        key={userID}
        accessLevel={accessLevel}
        hasTokens={hasTokens}
        isBroadcasterChat={isBroadcasterChat}
        isBroadcasting={isBroadcasting}
        isFanclubSubscribed={isFanclubSubscribed}
        isRoomModerator={isRoomModerator}
        isSilenced={isSilenced}
        isStarred={isStarred}
        isUserList={isUserList}
        originalUsername={originalUsername}
        profileImage={profileImage}
        recentSpendingTier={recentSpendingTier}
        username={username}
      />
    );
  }, [isBroadcasterChat, isUserList, username]);

  const recentSpendingTierLegendTriggerElement = useMemo(() => (
    <Tooltip
      arrow={true}
      placement="bottom"
      title={translate('v2broadcast.user.legend.tooltip')}
    >
      <IconButton
        className={styles.recentSpendingTierToggle}
        onClick={onToggleRecentSpendingTierLegend}
      >
        <HelpOutlineIcon />
      </IconButton>
    </Tooltip>
  ), [onToggleRecentSpendingTierLegend]);

  const chatTitle = useMemo(() => {
    if (isBroadcasterChat) {
      const classes = classNames(styles.chatTitle, styles.broadcasterChatTitle);

      return (
        <h5 className={classes}>
          {recentSpendingTierLegendTriggerElement}
          <span>
            {translate('v2broadcast.chattabs.userlist.all.users')}
          </span>
        </h5>
      );
    }

    const displayExternalCount = Boolean(ProfileStore.realityShow && externalViewersCount);

    return (
      <Fragment>
        {displayExternalCount && (
          <h5 className={styles.externalTitle}>
            <span>
              {translate('v2profile.external.users')}
              {' - '}
              {externalViewersCount}
              {' '}
              {translate('v2profile.chat.user.chatting')}
            </span>
            <Tooltip title="Tempted">
              <img alt="temptedLogo" src={temptedLogo} />
            </Tooltip>
            <Tooltip title="Naked News">
              <img alt="nnLogo" src={nnLogo} />
            </Tooltip>
          </h5>
        )}

        <h5 className={styles.chatTitle}>
          <span>
            {translate('v2profile.chat.user.list')}
            {' - '}
            {userCountLight}
            {' '}
            {translate('v2profile.chat.user.chatting')}
          </span>
        </h5>
      </Fragment>
    );
  }, [
    externalViewersCount,
    isBroadcasterChat,
    recentSpendingTierLegendTriggerElement,
    userCountLight,
  ]);

  const users = finalUserList.map(mapUserToItem);

  return (
    <div className={styles.UserListTab}>
      {chatTitle}

      <div className={styles.userList}>
        <ul className={styles.userList}>
          <UserListItem
            isBroadcasterChat={isBroadcasterChat}
            isStarred={false}
            isThePerformer={true}
            isUserList={isUserList}
            originalUsername={isBroadcasterChat ? username : ProfileStore.performerDetails.username}
            profileImage={getProfileImage(profileName)}
            username={username}
          />
          {users}
        </ul>
      </div>
      <RecentSpendingTierLegend
        isOpen={isRecentSpendingTierLegendOpen}
        onToggle={onToggleRecentSpendingTierLegend}
      />
    </div>
  );
};

UserListTab.propTypes = {
  isBroadcasterChat: PropTypes.bool,
  isRecentSpendingTierLegendOpen: PropTypes.bool,
  isUserList: PropTypes.bool,
  username: PropTypes.string,
  onToggleRecentSpendingTierLegend: PropTypes.func,
};

UserListTab.defaultProps = {
  isBroadcasterChat: false,
  isRecentSpendingTierLegendOpen: false,
  isUserList: false,
  username: undefined,
  onToggleRecentSpendingTierLegend: () => undefined,
};

export default observer(UserListTab);
